/**
 * Job Board catalog — Feb 2026.
 *
 * Each board belongs to one of three scopes:
 *  - 'platform'      : Jobasti hosts a public XML feed. No per-company creds needed.
 *  - 'organization'  : each company connects its own credentials in Settings.
 *  - 'coming_soon'   : will be wired through Vonq HAPI once Jobasti is a partner.
 *
 * `country` is purely informational. `logoUrl` uses Simple Icons CDN.
 */
export const JOB_BOARDS = [
  // ── Platform-managed (free public feeds) ──────────────────────────────────
  {
    id: 'indeed',
    name: 'Indeed',
    country: 'Global',
    scope: 'platform',
    description: '#1 job site in the world by visitor traffic.',
    color: '#003A9B',
    logoUrl: 'https://cdn.simpleicons.org/indeed/003A9B',
  },
  {
    id: 'glassdoor',
    name: 'Glassdoor',
    country: 'Global',
    scope: 'platform',
    description: 'Where transparency around pay & culture matters.',
    color: '#0CAA41',
    logoUrl: 'https://cdn.simpleicons.org/glassdoor/0CAA41',
  },
  {
    id: 'adzuna',
    name: 'Adzuna',
    country: 'UK · DE',
    scope: 'platform',
    description: 'Aggregator covering both UK and German listings.',
    color: '#0F9D58',
    logoUrl: null,
  },

  // ── Company-managed (per-org credentials) ─────────────────────────────────
  {
    id: 'bundesagentur',
    name: 'Bundesagentur für Arbeit',
    country: 'DE',
    scope: 'organization',
    description: "Germany's federal job board. Free, requires HR-BA-XML contract.",
    color: '#D4002F',
    logoUrl: null,
  },
  {
    id: 'linkedin',
    name: 'LinkedIn Basic Jobs',
    country: 'Global',
    scope: 'organization',
    description: 'Free Basic Jobs via per-company XML feed (Job Wrapping).',
    color: '#0A66C2',
    logoUrl: null,
    inlineGlyph: 'linkedin',
  },

  // ── Coming Soon (Vonq HAPI dependent) ─────────────────────────────────────
  { id: 'stepstone',  name: 'StepStone',  country: 'DE',     scope: 'coming_soon', description: "Germany's leading career site.", color: '#0094C8', logoUrl: null },
  { id: 'xing',       name: 'XING Jobs',  country: 'DE',     scope: 'coming_soon', description: 'DACH professional network.', color: '#026466', logoUrl: 'https://cdn.simpleicons.org/xing/026466' },
  { id: 'reed',       name: 'Reed.co.uk', country: 'UK',     scope: 'coming_soon', description: "The UK's biggest recruitment platform.", color: '#E11A2B', logoUrl: null },
  { id: 'monster',    name: 'Monster',    country: 'Global', scope: 'coming_soon', description: 'Global jobs site.', color: '#6E45A4', logoUrl: null },
  { id: 'totaljobs',  name: 'Totaljobs',  country: 'UK',     scope: 'coming_soon', description: 'UK-wide jobs across all industries.', color: '#E60028', logoUrl: null },
  { id: 'cv-library', name: 'CV-Library', country: 'UK',     scope: 'coming_soon', description: "One of the UK's top independent job boards.", color: '#0B6BB6', logoUrl: null },
  { id: 'finn',       name: 'Finn.no',    country: 'NO',     scope: 'coming_soon', description: "Norway's #1 classifieds & jobs site.", color: '#005AA7', logoUrl: null },
  { id: 'jobindex',   name: 'Jobindex',   country: 'DK',     scope: 'coming_soon', description: "Denmark's largest job portal.", color: '#1B3A8B', logoUrl: null },
  { id: 'pracuj',     name: 'Pracuj.pl',  country: 'PL',     scope: 'coming_soon', description: "Poland's biggest job board.", color: '#FFB400', logoUrl: null },
  { id: 'cv-online',  name: 'CV Online',  country: 'Baltic', scope: 'coming_soon', description: 'Estonia / Latvia / Lithuania jobs.', color: '#00AEEF', logoUrl: null },
];

export const JOB_BOARD_IDS = JOB_BOARDS.map((b) => b.id);
export const PLATFORM_BOARDS = JOB_BOARDS.filter((b) => b.scope === 'platform');
export const COMPANY_BOARDS  = JOB_BOARDS.filter((b) => b.scope === 'organization');
export const COMING_SOON_BOARDS = JOB_BOARDS.filter((b) => b.scope === 'coming_soon');

/**
 * Extra editorial metadata per board (regions covered, countries it performs
 * best in, and the sectors it's strongest for). Much of this is indicative /
 * dummy for now — refine as real board-performance data lands.
 */
export const BOARD_META = {
  indeed:        { regions: ['Global'],          strongCountries: ['United States', 'United Kingdom', 'Germany', 'Canada', 'Australia'], sectors: ['All sectors', 'Technology', 'Retail', 'Healthcare', 'Logistics'] },
  glassdoor:     { regions: ['Global'],          strongCountries: ['United States', 'United Kingdom', 'Canada'],                          sectors: ['Technology', 'Finance', 'Consulting'] },
  adzuna:        { regions: ['Europe'],          strongCountries: ['United Kingdom', 'Germany'],                                          sectors: ['Technology', 'Engineering', 'Finance'] },
  bundesagentur: { regions: ['Europe'],          strongCountries: ['Germany'],                                                            sectors: ['All sectors', 'Trades', 'Healthcare', 'Administration'] },
  linkedin:      { regions: ['Global'],          strongCountries: ['United States', 'United Kingdom', 'Germany', 'India'],                sectors: ['Technology', 'Finance', 'HR', 'Consulting', 'Executive'] },
  stepstone:     { regions: ['Europe'],          strongCountries: ['Germany', 'Austria'],                                                 sectors: ['Technology', 'Engineering', 'Finance', 'Sales'] },
  xing:          { regions: ['Europe'],          strongCountries: ['Germany', 'Austria', 'Switzerland'],                                  sectors: ['HR', 'Sales', 'Management', 'Technology'] },
  reed:          { regions: ['Europe'],          strongCountries: ['United Kingdom', 'Ireland'],                                          sectors: ['Finance', 'HR', 'Administration', 'Technology'] },
  monster:       { regions: ['Global'],          strongCountries: ['United States', 'France', 'Germany'],                                 sectors: ['All sectors', 'Technology', 'Healthcare'] },
  totaljobs:     { regions: ['Europe'],          strongCountries: ['United Kingdom'],                                                     sectors: ['Technology', 'Engineering', 'Sales'] },
  'cv-library':  { regions: ['Europe'],          strongCountries: ['United Kingdom'],                                                     sectors: ['Engineering', 'Construction', 'Logistics', 'Technology'] },
  finn:          { regions: ['Europe'],          strongCountries: ['Norway'],                                                             sectors: ['Technology', 'Engineering', 'Public sector'] },
  jobindex:      { regions: ['Europe'],          strongCountries: ['Denmark'],                                                            sectors: ['Technology', 'Finance', 'Public sector'] },
  pracuj:        { regions: ['Europe'],          strongCountries: ['Poland'],                                                             sectors: ['Technology', 'Manufacturing', 'Finance'] },
  'cv-online':   { regions: ['Europe'],          strongCountries: ['Estonia', 'Latvia', 'Lithuania'],                                     sectors: ['Technology', 'Manufacturing', 'Sales'] },
};

/** All regions present across the catalog (for the Job Boards page filter). */
export const ALL_BOARD_REGIONS = Array.from(new Set(Object.values(BOARD_META).flatMap((m) => m.regions))).sort();
/** All sectors present across the catalog. */
export const ALL_BOARD_SECTORS = Array.from(new Set(Object.values(BOARD_META).flatMap((m) => m.sectors))).sort();
